import React, { useEffect, useState } from "react";
import LinearProgress from "@mui/material/LinearProgress";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import { getSingleProduct, getSingleProductBySlug } from "../api/apiCalls";
import { Link, useNavigate, useParams } from "react-router-dom";

import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore, { Navigation, Thumbs } from "swiper";
import "swiper/swiper-bundle.min.css";
import "swiper/swiper.min.css";

import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import CameraIcon from "@mui/icons-material/Camera";
import MemoryIcon from "@mui/icons-material/Memory";
import Battery0BarIcon from "@mui/icons-material/Battery0Bar";
import WatchIcon from "@mui/icons-material/Watch";
import OrderProduct from "./OrderProduct";
import {
  createBreadcrumbSchema,
  createProductSchema,
  usePageSeo,
} from "../utils/seo";
import { getPhoneUrl, getProductSlug } from "../utils/productUrl";

SwiperCore.use([Navigation, Thumbs]);

function SingleProduct() {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [colorC, setcolorC] = useState("");
  const [storageC, setstorageC] = useState("");
  const [price, setPrice] = useState("");
  const [showAdded, setShowAdded] = useState(false);

  const { productId, slug } = useParams();
  const navigate = useNavigate();
  const isLegacyUrl = Boolean(productId);
  const canonicalPath = product ? getPhoneUrl(product) : `/telefon/${slug || ""}`;

  usePageSeo({
    title: product ? `${product.title} | Buzz Mobile` : "Телефон | Buzz Mobile",
    description: product
      ? `Купете ${product.title} од Buzz Mobile.`
      : "Погледнете ги мобилните телефони од Buzz Mobile.",
    canonicalPath,
    image: product?.img?.[0],
    type: "product",
    structuredData: product
      ? [
          createProductSchema({ product, productPath: canonicalPath }),
          createBreadcrumbSchema([
            { name: "Buzz Mobile", path: "/" },
            { name: "Телефони", path: "/telefoni/Apple" },
            { name: product.title, path: canonicalPath },
          ]),
        ]
      : null,
  });

  useEffect(() => {
    let isMounted = true;

    window.scrollTo(0, 0);
    setLoading(true);
    setProduct(null);

    const productRequest = isLegacyUrl
      ? getSingleProduct(productId)
      : getSingleProductBySlug(slug);

    productRequest.then(
      (res) => {
        if (!isMounted) {
          return;
        }

        setProduct(res || null);
        setLoading(false);
      },
      (err) => {
        console.log(err);
        if (isMounted) {
          setLoading(false);
        }
      }
    );

    return () => {
      isMounted = false;
    };
  }, [isLegacyUrl, productId, slug]);

  useEffect(() => {
    if (!product) {
      return;
    }

    const productPath = getPhoneUrl(product);

    if (isLegacyUrl || slug !== getProductSlug(product)) {
      navigate(productPath, { replace: true });
    }
  }, [isLegacyUrl, navigate, product, slug]);

  useEffect(() => {
    if (!product) {
      return;
    }

    setcolorC(product.color?.[0] || "");
    setstorageC(product.priceStorage?.[0]?.storage || "");
    setPrice(product.priceStorage?.[0]?.price || "");
  }, [product]);

  const getPrice = () => {
    if (!product?.priceStorage?.length) {
      return product?.price || 0;
    }

    const filteredResult = product.priceStorage.find(
      (item) => item.storage === storageC
    );

    return filteredResult?.price || product.priceStorage[0].price;
  };

  const handlePriceStorage = (storage) => {
    setPrice(storage.price);
    setstorageC(storage.storage);
  };

  const handleAddToCart = () => {
    if (!product) {
      return;
    }

    let current = sessionStorage.getItem("cart");
    current = current ? JSON.parse(current) : [];

    const item = {
      id: product._id,
      title: product.title,
      slug: product.slug,
      price: getPrice(),
      storage: storageC,
      color: colorC,
      img: product.img?.[0],
      categories: product.categories?.[0] || "telefoni",
      quantity: 1,
    };

    const sameConfigIndex = current.findIndex(
      (entry) =>
        entry.id === item.id &&
        (entry.storage || "") === (item.storage || "") &&
        (entry.color || "") === (item.color || "")
    );

    if (sameConfigIndex >= 0) {
      current[sameConfigIndex].quantity += 1;
    } else {
      current.push(item);
    }

    sessionStorage.setItem("cart", JSON.stringify(current));
    setShowAdded(true);
    setTimeout(() => setShowAdded(false), 2000);
  };

  if (loading) {
    return (
      <div className="single__product__container container">
        <LinearProgress />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="single__product__container container">
        <div className="single__product__info-card">
          <p className="sp__title">Производот не е достапен.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="single__product__container container">
      <div className="single__product__wrapper">
        <section className="single__product__media-card">
          <div className="single__product__media-copy">
            <span className="single__product__eyebrow">{product.brand}</span>
          </div>

          <div className="single__product__images">
            <Swiper
              style={{
                "--swiper-navigation-color": "#1e1e29",
                "--swiper-pagination-color": "#1e1e29",
                width: "100%",
              }}
              spaceBetween={10}
              navigation={true}
              thumbs={{
                swiper:
                  thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null,
              }}
              modules={[Navigation, Thumbs]}
              className="single__product__gallery"
            >
              {product.img?.map((item, index) => (
                <SwiperSlide key={index}>
                  <img src={item} alt={product.title} />
                </SwiperSlide>
              ))}
            </Swiper>

            <Swiper
              onSwiper={setThumbsSwiper}
              slidesPerView={3}
              spaceBetween={12}
              modules={[Navigation, Thumbs]}
              className="single__product__thumbs"
              style={{ width: "100%" }}
            >
              {product.img?.map((item, index) => (
                <SwiperSlide key={index}>
                  <img src={item} alt={product.title} height={100} />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </section>

        <section className="single__product__info-card">
          <div className="single__product__hero">
            <div className="single__product__title">
              <p className="sp__title">{product.title}</p>
            </div>
          </div>

          <div className="single__product__specs">
            <p className="sp__specs">Спецификации</p>
            <div className="sp__wrapper">
              <div className="single__product__specs-item">
                {product.title.includes("Watch") ? <WatchIcon /> : <PhoneIphoneIcon />}
                <span className="single__product__spec-label">
                  {product.title.includes("Watch") ? "Модел" : "Дисплеј"}
                </span>
                <p>{product.display}</p>
              </div>
              <div className="single__product__specs-item">
                <CameraIcon />
                <span className="single__product__spec-label">Камера</span>
                <p>{product.camera}</p>
              </div>
              <div className="single__product__specs-item">
                <MemoryIcon />
                <span className="single__product__spec-label">Меморија / Чип</span>
                <p>{product.ram}</p>
              </div>
              <div className="single__product__specs-item">
                <Battery0BarIcon />
                <span className="single__product__spec-label">Батерија</span>
                <p>{product.battery}</p>
              </div>
            </div>
          </div>

          <div className="single__product__purchase">
            <div className="sp__choice">
              <div className="single__product__colors">
                <p>Бои:</p>
                <div className="single__product__swatches">
                  {product.color?.map((color, index) => (
                    <button
                      key={index}
                      type="button"
                      value={color}
                      style={{ backgroundColor: `${color}` }}
                      onClick={() => setcolorC(color)}
                      className={`sp__colors ${
                        color === colorC ? "active-color" : ""
                      }`}
                      aria-label={`Color option ${index + 1}`}
                    ></button>
                  ))}
                </div>
              </div>

              <div className="single__product__storage">
                {product.title.includes("Watch") ? <p>Дијаметар:</p> : <p>Меморија:</p>}

                <div className="single__product__storage-list">
                  {product.priceStorage?.map((storage, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handlePriceStorage(storage)}
                      value={storage.storage}
                      className={
                        "sp__storage" +
                        " " +
                        (storage.storage === storageC ? "active-storage" : "")
                      }
                    >
                      {storage.storage}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="single__product__summary">
              <div className="single__product__price">
                <p className="sp__price">
                  {parseFloat(getPrice()).toLocaleString("en")} <span>ден.</span>
                </p>
              </div>
            </div>

            <div className="single__product__actions">
              <button
                type="button"
                className="single__product__btn single__product__btn--secondary"
                onClick={handleAddToCart}
              >
                <AddShoppingCartIcon fontSize="small" />
                <span>Додај во кошничка</span>
              </button>

              <OrderProduct
                title={product.title}
                color={colorC}
                storage={storageC}
                price={price}
                img={product.img?.[0]}
              />
            </div>
          </div>
        </section>
      </div>

      {showAdded ? (
        <div className="addtocart__popup">
          <Stack sx={{ width: "100%" }} spacing={2}>
            <Alert severity="success">
              <p>
                {product.title} е додаден во{" "}
                <Link to="/cart" className="links">
                  кошничката
                </Link>
              </p>
            </Alert>
          </Stack>
        </div>
      ) : null}
    </div>
  );
}

export default SingleProduct;
